/**
 * One delegated listener file for PTA interaction analytics.
 *
 * ## Why not `gtag`
 *
 * `window.gtag` does not exist on this storefront. Analytics reaches the store through the
 * Google & YouTube channel, which Shopify runs as an app pixel inside an iframe, and theme
 * code cannot reach into that sandbox. `Shopify.analytics.publish` crosses it: the custom
 * pixel "PTA Custom Events" in the admin subscribes by event name and forwards to GA4.
 *
 * **Without a matching subscription in that pixel, every call here is a silent no-op.** That
 * is not hypothetical — `finder_click` worked for about five years on Debut, where a
 * theme-level tag made `window.gtag` real, and went quiet the moment analytics moved into the
 * app pixel. No error, no missing page, just a metric that flattened, unnoticed for years.
 *
 * ## One event name, on purpose
 *
 * Everything here publishes `pta_interaction` and distinguishes itself with an `action`
 * param, rather than taking one event name per interaction. Two reasons, both about the
 * failure above:
 *
 * - The admin pixel is subscribed once. Adding a tracking point later is theme-only work and
 *   cannot introduce a new silently-unsubscribed name.
 * - GA4 registers `action` / `surface` / `label` / `platform` as four custom dimensions total,
 *   however many tracking points are added, rather than a fresh set per event.
 *
 * `finder_click` and `pta_dimensions_opened` keep their own names — they predate this, they
 * are already subscribed, and they carry history worth not breaking.
 *
 * ## Adding a tracking point
 *
 * For anything clickable, no JavaScript is needed at all — put the attributes on the element:
 *
 *     data-pta-action="guide_click"
 *     data-pta-surface="finder_guides"
 *     data-pta-label="DeWalt"
 *     data-pta-platform="DeWalt 20V MAX"     (optional)
 */

const EVENT = 'pta_interaction';

/** GA4 truncates param values at 100 characters; do it here so reports are not silently cut. */
const MAX_VALUE = 100;

/**
 * @param {string} action
 * @param {Record<string, string | null | undefined>} [params]
 */
function publish(action, params = {}) {
  if (typeof window.Shopify?.analytics?.publish !== 'function') return;

  /** @type {Record<string, string>} */
  const payload = { action };

  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') continue;

    payload[key] = String(value).slice(0, MAX_VALUE);
  }

  window.Shopify.analytics.publish(EVENT, payload);
}

/**
 * Anything carrying `data-pta-action`.
 *
 * Bound to `click` rather than to each link's navigation: the publish is synchronous into the
 * sandbox, and unlike the finder — which delays its own redirect by 200 ms — these are plain
 * anchors whose navigation the browser starts after the handler returns.
 */
document.addEventListener('click', (event) => {
  const target = event.target;

  if (!(target instanceof Element)) return;

  const el = target.closest('[data-pta-action]');

  if (!(el instanceof HTMLElement)) return;

  publish(el.dataset.ptaAction ?? '', {
    surface: el.dataset.ptaSurface,
    label: el.dataset.ptaLabel,
    platform: el.dataset.ptaPlatform,
  });
});

/**
 * FAQ questions, across every accordion surface at once.
 *
 * ## Capture, because `toggle` does not bubble
 *
 * Same shape as the dimensions dialog in `blocks/pta-adapter-specs.liquid`, and for the same
 * reason: `toggle` is a non-bubbling event, so a listener on `document` never sees it in the
 * bubble phase. The capture phase runs root-down for every event regardless.
 *
 * ## Opening only
 *
 * `toggle` fires on close as well, and a close is not interest — counting both would double
 * every engaged reader and make a question that is opened and re-read look twice as popular
 * as one opened once.
 *
 * ## The label is a key, never the question text
 *
 * The store renders in fifteen languages. Sending `summary.textContent` would scatter one
 * question across fifteen labels that never aggregate. `data-pta-question` carries a stable
 * slug instead — the locale key for the static surfaces, and a slug threaded through
 * `question~~answer~~slug` for the two that build their rows from rendered prose.
 */
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;

    if (!(details instanceof HTMLDetailsElement)) return;
    if (!details.classList.contains('pta-accordion__item')) return;
    if (!details.open) return;

    publish('faq_open', {
      surface: details.dataset.ptaSurface,
      label: details.dataset.ptaQuestion,
      platform: details.dataset.ptaPlatform,
    });
  },
  true
);

/**
 * "See it in action" — the YouTube facade in `blocks/pta-product-video.liquid`.
 *
 * Read from the facade's own `data-video-id` rather than given `data-pta-*` attributes, since
 * the id it already carries is exactly the label wanted.
 *
 * **This counts starts, not watches.** The block deliberately renders a thumbnail and swaps in
 * the real player on click, because a live embed pulls roughly a megabyte of third-party
 * script on every product page. Watch-time would need the YouTube IFrame Player API, which is
 * the thing the facade exists to avoid — so the question this answers is "was it opened, and
 * which one", and that is where it should stay.
 *
 * No product identifier is sent: the page's own URL already answers "which adapter", and a handle
 * param would be a second copy of something that stays consistent only while there is one of it.
 *
 * That rests on the pixel attaching `page_location` from `event.context`, because a pixel is
 * sandboxed and anything gtag infers from its own `document` describes the sandbox rather than the
 * storefront. See `documentations/analytics.md`. If that helper is ever dropped from the pixel,
 * this event loses the ability to say which product it happened on.
 */
document.addEventListener('click', (event) => {
  const target = event.target;

  if (!(target instanceof Element)) return;

  const facade = target.closest('.pta-video__facade');

  if (!(facade instanceof HTMLElement)) return;

  publish('video_play', { surface: 'product_video', label: facade.dataset.videoId });
});
